'use client';

import { useMemo, useRef } from 'react';
import type { TerraUIMessage } from '@terra/shared-dto-chat-types';
import {
  processMessages,
  resolveChatHasActiveNeoAttackTracking,
  type ProcessedMessage,
} from '../utils/message-processing';
import { computeHasPendingHITL } from '../utils/pending-hitl';
import { retractSupersededModelOutput } from '../utils/retract-model-output';

type CacheEntry = {
  messages: TerraUIMessage[];
  neoAttackTrackingIdentity: string;
  stableIsLoading: boolean;
  processIsLoading: boolean;
  hideInlinePlanAndTodos: boolean;
  processedMessages: ProcessedMessage[];
};

function hasSameMessageContent({
  previous,
  next,
}: {
  previous: TerraUIMessage[];
  next: TerraUIMessage[];
}): boolean {
  return (
    previous.length === next.length &&
    next.every((message, index) => {
      const previousMessage = previous[index];

      return (
        previousMessage === message ||
        (previousMessage !== undefined &&
          previousMessage.id === message.id &&
          previousMessage.metadata === message.metadata &&
          previousMessage.parts.length === message.parts.length &&
          message.parts.every(
            (part, partIndex) => part === previousMessage.parts[partIndex],
          ))
      );
    })
  );
}

export function useProcessedMessages({
  messages,
  isLoading,
  streamIsLoading,
  hasActiveNeoAttackTracking: hasActiveNeoAttackTrackingInput,
  neoAttackTrackingIdentity: neoAttackTrackingIdentityInput,
  hideInlinePlanAndTodos = false,
}: {
  messages: TerraUIMessage[];
  isLoading: boolean;
  streamIsLoading?: boolean;
  hasActiveNeoAttackTracking?: boolean;
  neoAttackTrackingIdentity?: string;
  hideInlinePlanAndTodos?: boolean;
}): {
  processedMessages: ProcessedMessage[];
  stableIsLoading: boolean;
} {
  const cacheRef = useRef<CacheEntry | null>(null);
  const neoAttackTrackingCacheRef = useRef<{
    identity: string;
    result: boolean;
  } | null>(null);

  return useMemo(() => {
    const streamLoading = streamIsLoading ?? isLoading;
    const hasPendingHITL =
      !isLoading && messages.length > 0
        ? computeHasPendingHITL(messages)
        : false;
    const stableIsLoading = isLoading || hasPendingHITL;
    const processIsLoading = streamLoading || hasPendingHITL;
    const hasActiveNeoAttackTrackingFromInput =
      hasActiveNeoAttackTrackingInput !== undefined &&
      neoAttackTrackingIdentityInput !== undefined;
    const {
      result: resolvedHasActiveNeoAttackTracking,
      cache: neoAttackTrackingCache,
    } = hasActiveNeoAttackTrackingFromInput
      ? {
          result: hasActiveNeoAttackTrackingInput,
          cache: {
            identity: neoAttackTrackingIdentityInput,
            result: hasActiveNeoAttackTrackingInput,
          },
        }
      : resolveChatHasActiveNeoAttackTracking({
          messages,
          cache: neoAttackTrackingCacheRef.current,
        });

    if (!hasActiveNeoAttackTrackingFromInput) {
      neoAttackTrackingCacheRef.current = neoAttackTrackingCache;
    }

    const hasActiveNeoAttackTracking = resolvedHasActiveNeoAttackTracking;

    const neoAttackTrackingIdentity = neoAttackTrackingCache.identity;
    const prev = cacheRef.current;

    if (
      prev &&
      hasSameMessageContent({ previous: prev.messages, next: messages }) &&
      prev.neoAttackTrackingIdentity === neoAttackTrackingIdentity &&
      prev.stableIsLoading === stableIsLoading &&
      prev.processIsLoading === processIsLoading &&
      prev.hideInlinePlanAndTodos === hideInlinePlanAndTodos
    ) {
      return { processedMessages: prev.processedMessages, stableIsLoading };
    }

    const processedMessages = processMessages(
      retractSupersededModelOutput(messages),
      {
        isLoading: processIsLoading,
        hasActiveNeoAttackTracking,
        hideInlinePlanAndTodos,
      },
    );

    cacheRef.current = {
      messages,
      neoAttackTrackingIdentity,
      stableIsLoading,
      processIsLoading,
      hideInlinePlanAndTodos,
      processedMessages,
    };

    return { processedMessages, stableIsLoading };
  }, [
    messages,
    isLoading,
    streamIsLoading,
    hasActiveNeoAttackTrackingInput,
    neoAttackTrackingIdentityInput,
    hideInlinePlanAndTodos,
  ]);
}
